import React, { useCallback, useEffect, useMemo } from 'react';
import { useQuery } from 'react-query';
import '@ant-design/compatible/assets/index.css';
import {
  Tabs,
  Col,
  Row,
  Select,
  DatePicker,
  Divider,
  Form,
  Input,
  Card,
  InputNumber,
} from 'antd';

import DF from 'DisplayFramework';
import { FormInstance } from 'antd/lib/form';
import GetLabel from 'DisplayFramework/components/GetLabel';
import FormInput from 'components/FormInput';
import {
  AssetServiceUseResponse,
  getAssetServicesListByAssetid,
  getAssetServiceUseListByAssocid,
} from 'services/assets';
import { isNil, maxBy } from 'lodash';
import ThirdPartyInput from 'components/ThirdPartyInput';
import Value from 'components/Value';
import InputCurrency from 'components/InputCurrency';
import DataGrid from 'components/DataGrid';
import { DataGridColDef } from 'components/DataGrid/DataGrid';
import { indexBy } from 'utils/utils2';
import { toCurrency, toRate } from 'utils/utils';
import TextArea from 'antd/lib/input/TextArea';
import UtinaCard from 'components/UtinaCard';

const { TabPane } = Tabs;

export type TabTPStatusProps = {
  objectid?: number;
  form: FormInstance;
  setSuccessful: any;
};

const TabAssetServiceUseRequest = ({
  objectid,
  form,
  setSuccessful,
}: TabTPStatusProps) => {
  const { data: assetServices, refetch: refetchassetServices } = useQuery(
    ['getAssetServicesListByAssetid', { assetid: objectid }],
    () =>
      getAssetServicesListByAssetid({ assetid: objectid! }).then(
        (res) => res.list || []
      ),
    {
      enabled: !isNil(objectid),
    }
  );

  const fieldName = 'serviceuselist';

  const getAssetServiceUseHistory = (associd: number) => {
    getAssetServiceUseListByAssocid({ associd: associd }).then((res) => {
      if (res.code === '200') {
        form.setFieldValue('serviceuselist', res.list);
        let lastUseDate = maxBy(res.list, 'validationdate')?.validationdate;
        form.setFieldValue('lastUseDate', lastUseDate ?? '');

        let lastkilometerscounter = maxBy(
          res.list,
          'kilometerscounter'
        )?.kilometerscounter;
        form.setFieldValue('lastkilometerscounter', lastkilometerscounter ?? 0);
      }
    });
  };

  const handleAmountsChange = () => {
    let maxAmount = form.getFieldValue('servusemaxamount');
    let minAmount = form.getFieldValue('servuseminamount');
    let reqAmount = form.getFieldValue('requestedamount');
    let deducPerc = form.getFieldValue('servusedeductionpercenrage');
    let deducAmount = form.getFieldValue('servusedeductionamount');

    let acceptedamount = 0;
    let suppliervalidatedamount = 0;
    let useramounttobeinvoiced = 0;

    if (reqAmount <= maxAmount) {
      acceptedamount = reqAmount;
    } else {
      acceptedamount = maxAmount;
    }

    if (reqAmount <= minAmount) {
      acceptedamount = 0;
    }

    suppliervalidatedamount = acceptedamount;

    if (deducAmount !== null) {
      useramounttobeinvoiced = deducAmount;
    }

    if (deducPerc !== null) {
      useramounttobeinvoiced =
        useramounttobeinvoiced + (acceptedamount * deducPerc) / 100;
    }

    form.setFieldsValue({
      acceptedamount: acceptedamount,
      suppliervalidatedamount: suppliervalidatedamount,
      useramounttobeinvoiced: useramounttobeinvoiced,
    });
  };

  useEffect(() => {
    form.setFieldsValue({
      [fieldName]: [],
      event: 'tabAssetServiceUseRequest',
    });
  }, []);

  const getName = useCallback(
    (name: string) => (record: any) => {
      const serviceuse = form.getFieldValue(fieldName);
      const index = indexBy(serviceuse, 'useid')[record.useid];
      return [fieldName, index, name];
    },
    [form]
  );
  const columns = useMemo<DataGridColDef<any>[]>(
    () => [
      {
        dataIndex: 'creationdate',

        width: 140,
      },
      {
        dataIndex: 'requestdate',

        width: 140,
      },
      {
        dataIndex: 'validationdate',

        width: 140,
      },
      {
        dataIndex: 'suppliername',

        width: 200,
      },
      {
        dataIndex: 'requestedamount',
        render: (value, record) => toCurrency(value),
        width: 140,
      },
      {
        dataIndex: 'acceptedamount',
        render: (value, record) => toCurrency(value),

        width: 140,
      },
      {
        dataIndex: 'useramounttobeinvoiced',
        render: (value, record) => toCurrency(value),
        width: 140,
      },
      {
        dataIndex: 'suppliervalidatedamount',
        render: (value, record) => toCurrency(value),
        width: 140,
      },
      {
        dataIndex: 'poorderreference',

        width: 140,
      },
      {
        dataIndex: 'requeststatuslabel',

        width: 140,
        fixed: 'right',
      },
    ],
    [form, getName]
  );

  return (
    <DF.Provider pagcode="assetuserequest">
      <Card>
        <Row>
          <Col span={24}>
            <Tabs animated defaultActiveKey="1" type="line">
              <TabPane tab={<GetLabel label="service" />} key="1" forceRender>
                <Row gutter={30}>
                  <FormInput comcode="assetservice" col md={8}>
                    <Select
                      onSelect={(elem: number) => {
                        let serv = assetServices?.find(
                          (e) => e.associd === elem
                        )!;


                        form.setFieldsValue({
                          servcode: serv.servcode,
                          associd: serv.associd,
                          assetid: serv.assetid,
                          cteid: serv.cteid,
                          currcode: localStorage.getItem('currcode'),
                          servuseunitlabel: serv.servuseunitlabel,
                          servusefrequencylabel: serv.servusefrequencylabel,
                          servuseqty: serv.servuseqty,
                          servuseminamount: serv.servuseminamount,
                          servusemaxamount: serv.servusemaxamount,
                          servusecoverage: serv.servusecoverage,
                          servusedeductionamount: serv.servusedeductionamount,
                          servusedeductionpercenrage:
                            serv.servusedeductionpercenrage,
                          servdescription: serv.servdescription,
                        });

                        getAssetServiceUseHistory(serv.associd);
                      }}
                      showSearch
                      allowClear
                    >
                      {assetServices?.map((serv, index) => (
                        <Select.Option value={serv.associd} key={index}>
                          {serv.servlabel}
                        </Select.Option>
                      ))}
                    </Select>
                  </FormInput>

                  <FormInput comcode="requestdate" col md={8}>
                    <DatePicker />
                  </FormInput>

                  <Divider />

                  <Row gutter={30}>
                    <Col md={24} style={{ paddingLeft: 30, paddingRight: 30 }}>
                      <Form.Item noStyle shouldUpdate>
                        {({ getFieldValue }) => {
                          const value: AssetServiceUseResponse[] =
                            getFieldValue('serviceuselist');

                          return (
                            <DataGrid
                              rowKey="useid"
                              dataSource={value}
                              columns={columns}
                              hideFilters
                              title="ListAssetServiceUseRequest"
                              hideExtraAction
                              hideMarginExtractData
                            />
                          );
                        }}
                      </Form.Item>
                    </Col>
                  </Row>
                </Row>
              </TabPane>
              <TabPane tab={<GetLabel label="request" />} key="2" forceRender>
                <Row gutter={4}>
                  <Col span={14}>
                  <Row gutter={12}>
                    <FormInput 
                      name="supplierid" 
                      col 
                      md={12} 
                      shouldUpdate
                      wrapperCol={{ md: 12 }}
                      labelCol={{ md: 10 }}
                      labelAlign="left"
                    >
                      <ThirdPartyInput
                        role="SUPPLIER"
                        onChange={(e: any) => {
                          form.setFieldsValue({
                            supplierid: e?.tpid,
                            suppliername: e?.tpname,
                            supplierreference: e?.tpreference,
                          });
                        }}
                      />
                    </FormInput>
                    <FormInput 
                      name="lastUseDate" 
                      col 
                      md={12} 
                      shouldUpdate
                      wrapperCol={{ md: 12 }}
                      labelCol={{ md: 10 }}
                      labelAlign="left"
                    >
                      <Value />
                    </FormInput>
                  </Row>
                  <Row gutter={12}>
                    <FormInput
                      name="lastkilometerscounter"
                      col
                      md={12}
                      shouldUpdate
                      wrapperCol={{ md: 12 }}
                      labelCol={{ md: 10 }}
                      labelAlign="left"
                    >
                      <Value />
                    </FormInput>

                    <FormInput
                      name="kilometerscounter"
                      col
                      md={12}
                      wrapperCol={{ md: 12 }}
                      labelCol={{ md: 10 }}
                      labelAlign="left"
                    >
                      <InputNumber />
                    </FormInput>
                  </Row>
                   
                  <Row gutter={12}>
                    <FormInput
                      name="requestedamount"
                      col
                      md={12}
                      wrapperCol={{ md: 12 }}
                      labelCol={{ md: 10 }}
                      labelAlign="left"
                    >
                      <InputCurrency
                        currcode={localStorage.getItem('currcode') ?? ''}
                        onChange={handleAmountsChange}
                      />
                    </FormInput>
                    <FormInput
                      name="acceptedamount"
                      col
                      md={12}
                      wrapperCol={{ md: 12 }}
                      labelCol={{ md: 10 }}
                      labelAlign="left"
                    >
                      <InputCurrency
                        currcode={localStorage.getItem('currcode') ?? ''}
                      />
                    </FormInput>
                  </Row>  
                  <Row gutter={12}>
                    <FormInput
                      name="suppliervalidatedamount"
                      col
                      md={12}
                      wrapperCol={{ md: 12 }}
                      labelCol={{ md: 10 }}
                      labelAlign="left"
                    >
                      <InputCurrency
                        currcode={localStorage.getItem('currcode') ?? ''}
                      />
                    </FormInput>
                    <FormInput
                      name="useramounttobeinvoiced"
                      col
                      md={12}
                      wrapperCol={{ md: 12 }}
                      labelCol={{ md: 10 }}
                      labelAlign="left"
                    >
                      <InputCurrency
                        currcode={localStorage.getItem('currcode') ?? ''}
                      />
                    </FormInput>
                  </Row>
                  <Row>
                    <FormInput
                      name="externalreference"
                      col
                      md={12}
                      wrapperCol={{ md: 12 }}
                      labelCol={{ md: 10 }}
                      labelAlign="left"
                    >
                      <Input />
                    </FormInput>
                  </Row>
                  <Row>
                  <FormInput name="servdescription" col md={8} shouldUpdate>
                    <Value />
                  </FormInput>
                  </Row>  
                  </Col>
                  <Col span={10}>
                    <UtinaCard comcode="Contract Conditions" level={5}>
                      <Row gutter={8}>                        
                        <FormInput 
                          name="servuseunitlabel" 
                          col 
                          md={12} 
                          shouldUpdate
                          wrapperCol={{ md: 12 }}
                          labelCol={{ md: 10 }}
                          labelAlign="left"
                        >
                          <Value />
                        </FormInput>
                        <FormInput
                          name="servusefrequencylabel"
                          col
                          md={12}
                          shouldUpdate
                        >
                          <Value />
                        </FormInput>
                      </Row>
                      <Row gutter={8}>
                        <FormInput 
                          name="servuseqty" 
                          col md={12} 
                          shouldUpdate
                          wrapperCol={{ md: 12 }}
                          labelCol={{ md: 10 }}
                          labelAlign="left"
                        >
                          <Value />
                        </FormInput>
                        <FormInput 
                          name="servusecoverage" 
                          col 
                          md={12} 
                          shouldUpdate
                          wrapperCol={{ md: 12 }}
                          labelCol={{ md: 10 }}
                          labelAlign="left"
                        >
                          <Value />
                        </FormInput>
                      </Row>
                      <Row gutter={8}>
                        <FormInput 
                          name="servuseminamount" 
                          col 
                          md={12} 
                          shouldUpdate
                          wrapperCol={{ md: 12 }}
                          labelCol={{ md: 10 }}
                          labelAlign="left"
                        >
                          <Value formatter={toCurrency}/>
                        </FormInput>
                        <FormInput 
                          name="servusemaxamount" 
                          col 
                          md={12} 
                          shouldUpdate
                          wrapperCol={{ md: 12 }}
                          labelCol={{ md: 10 }}
                          labelAlign="left"
                        >
                          <Value formatter={toCurrency}/>
                        </FormInput>
                      </Row>
                      <FormInput
                        name="servusedeductionamount"
                        md={12}
                        shouldUpdate
                      >
                        <Value formatter={toCurrency}/>
                      </FormInput>
                      <FormInput
                        name="servusedeductionpercenrage"
                        md={8}
                        shouldUpdate
                      >
                        <Value formatter={toRate}/>
                      </FormInput>
                    </UtinaCard>
                  </Col>
                                 
                  <FormInput name="usecomment" col md={14} shouldUpdate>
                    <TextArea rows={4} />
                  </FormInput>
                </Row>
              </TabPane>
            </Tabs>
          </Col>
        </Row>
      </Card>
    </DF.Provider>
  );
};
export default TabAssetServiceUseRequest;
